/**
 * Page clock: local wall time (Date.now) plus an offset to server time.
 *
 * Server time comes from the HTTP `Date` header of the rounds.json response, which nginx stamps
 * when it sends the response. The `serverTime` field inside rounds.json is written by the engine
 * once per ~5s tick, so it is 0-6s stale; it is only a fallback when the header is missing.
 * Each successful fetch replaces the offset (no smoothing), so a resumed device, an engine clock
 * change or a user changing the system clock is corrected by the next fetch. Only a sample with a
 * round trip over MAX_SAMPLE_RTT_S is skipped (see nextOffset). Wall time is used rather than
 * performance.now, which stops while the device sleeps on several platforms.
 *
 * MAX_OFFSET_S, MIN_SERVER_DATE_S, nextPlausibleOffset, parseDateHeader and their rejected-sample
 * log are copies of utils/clock.ts (the app clock), kept identical. This page does not use them yet:
 * api.ts reads the header with Date.parse and the page calls nextOffset, so the page checks neither
 * the header form nor the MAX_OFFSET_S bound.
 */

/**
 * The shown time may run up to this far ahead of a new reading without stepping back: the Date
 * header has 1s resolution. A reading further behind (sleep, clock change) is shown at once.
 */
export const DISPLAY_HOLD_S = 1.5;

/**
 * The Date header is floored to whole seconds, so on average the true stamp is 0.5s later. Without
 * this the clock runs ~0.5s slow and, with the chain closing at endAt - 1s, shows the round as
 * tradable for its last 1-2s while trades revert. Not applied to the `serverTime` field fallback:
 * that field is written once per engine tick and its staleness dwarfs the truncation.
 */
export const HEADER_FLOOR_BIAS_S = 0.5;

/**
 * A sample whose round trip took longer than this does not move the offset. The midpoint rule is
 * off by up to rtt/2, so a device that sleeps S seconds mid-request would put the clock S/2 off
 * (5 minutes for a 10-minute sleep) until the next fetch. Normal fetches take well under a second.
 */
export const MAX_SAMPLE_RTT_S = 3;

/**
 * A sample that puts server time more than this from the device clock does not move the offset.
 * parseDateHeader already drops a garbage Date header, so this bound only sets which device clocks
 * the server time corrects: a device clock up to 30 days off is still corrected.
 */
export const MAX_OFFSET_S = 30 * 86_400;

/** A Date header before 2026-01-01T00:00:00Z (epoch seconds) is not a server time. */
export const MIN_SERVER_DATE_S = 1_767_225_600;

// RFC 7231 IMF-fixdate ("Sat, 03 Oct 2026 10:00:00 GMT"): the only Date header form a sample takes.
const IMF_FIXDATE = /^[A-Z][a-z]{2}, \d{2} [A-Z][a-z]{2} \d{4} \d{2}:\d{2}:\d{2} GMT$/;

type ClockFeed = { serverTime: number; serverDate?: number };

/** Offset (server minus local wall time, seconds) from one fetch, using `serverDate` when known. */
export const clockOffset = (feed: ClockFeed, sentMs: number, receivedMs: number) => {
  const stamp = feed.serverDate !== undefined ? feed.serverDate + HEADER_FLOOR_BIAS_S : feed.serverTime;
  // The response was stamped between send and receive: assume the midpoint.
  const serverNow = stamp + (receivedMs - sentMs) / 2000;
  return serverNow - receivedMs / 1000;
};

/**
 * Offset after one fetch: the new sample, unless its round trip exceeded MAX_SAMPLE_RTT_S, in which
 * case the previous offset is kept. The first sample (no previous offset) is used even when slow:
 * an rtt/2 error is better than running on the raw device clock.
 */
export const nextOffset = (previous: number | undefined, feed: ClockFeed, sentMs: number, receivedMs: number) =>
  previous !== undefined && (receivedMs - sentMs) / 1000 > MAX_SAMPLE_RTT_S
    ? previous
    : clockOffset(feed, sentMs, receivedMs);

// True once a rejected sample is logged: one warning per page load is enough to diagnose a device.
let rejectionLogged = false;

/**
 * Logs the first sample of the session that the date floor or the offset bound rejects, so a device
 * clock more than MAX_OFFSET_S off can be diagnosed from the console.
 */
const logRejectedSample = (rejected: { header?: string | null; serverDate: number; offset?: number }) => {
  if (rejectionLogged) return;
  rejectionLogged = true;
  console.warn('[clock] rejected server Date sample', rejected);
};

/** Test-only: log the next rejected sample again. */
export const __resetRejectionLog = () => {
  rejectionLogged = false;
};

/**
 * nextOffset, except that a sample more than MAX_OFFSET_S from the device clock keeps the previous
 * offset (none before the first sample) and is logged. `header` is the raw Date header, for the log.
 */
export const nextPlausibleOffset = (
  previous: number | undefined,
  feed: ClockFeed,
  sentMs: number,
  receivedMs: number,
  header?: string | null
) => {
  const offset = clockOffset(feed, sentMs, receivedMs);
  if (Math.abs(offset) <= MAX_OFFSET_S) return nextOffset(previous, feed, sentMs, receivedMs);
  logRejectedSample({ header, serverDate: feed.serverDate ?? feed.serverTime, offset });
  return previous;
};

/**
 * A `Date` header in epoch seconds; undefined unless it is an IMF-fixdate not before MIN_SERVER_DATE_S.
 * Date.parse alone also takes "0" (the year 2000), other date forms and dates without a zone (read as
 * local time). A date before MIN_SERVER_DATE_S is logged.
 */
export const parseDateHeader = (header: string | null | undefined) => {
  const serverDate = header && IMF_FIXDATE.test(header) ? Date.parse(header) / 1000 : Number.NaN;
  if (serverDate < MIN_SERVER_DATE_S) logRejectedSample({ header, serverDate });
  // False for NaN: a date that Date.parse cannot read is dropped too.
  return serverDate >= MIN_SERVER_DATE_S ? serverDate : undefined;
};

/** Time to show after `shown`, given the clock now reads `now` (both in seconds). */
export const nextShownTime = (shown: number, now: number) => (now < shown && shown - now <= DISPLAY_HOLD_S ? shown : now);
